'use client'

import { useState } from 'react'
import { WebApplicationStructuredData } from '@/components/WebApplicationStructuredData'
import { SecretCreatorForm } from '@/components/SecretCreatorForm'
import { ResultCard } from '@/components/ResultCard'
import { FeaturesSection } from '@/components/FeaturesSection'
import { SecurityComparison } from '@/components/SecurityComparison'
import { TrustIndicators } from '@/components/TrustIndicators'

export default function HomePage() {
  const [result, setResult] = useState<{
    url: string
    passphrase?: string
    salt?: string
    iterations?: number
    secretType: 'text' | 'file'
  } | null>(null)

  const handleSuccess = (newResult: typeof result) => {
    setResult(newResult)
  }

  const handleReset = () => {
    setResult(null)
  }

  if (result) {
    return <ResultCard result={result} onReset={handleReset} />
  }

  return (
    <>
      <WebApplicationStructuredData />
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center py-8 sm:py-12 lg:py-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-mono font-semibold text-foreground mb-4 sm:mb-6">
              Share Secrets Securely with 
              <span className="text-primary block sm:inline"> Zero-Knowledge Encryption</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-4 max-w-3xl mx-auto px-4">
              Military-grade AES-GCM encryption happens in your browser. We never see your secrets or keys.
              Perfect for passwords, API keys, sensitive documents, and confidential information.
            </p>
            
            {/* Open Source Badge */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6 sm:mb-8">
              <a
                href="https://github.com/mdotk/safemonk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 bg-charcoal-700/50 hover:bg-charcoal-700 text-grey-200 rounded-full text-xs font-medium transition-colors border border-charcoal-600"
              >
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                </svg>
                100% Open Source
              </a>
              <a
                href="/verify"
                className="inline-flex items-center px-3 py-1.5 bg-charcoal-700/50 hover:bg-charcoal-700 text-grey-200 rounded-full text-xs font-medium transition-colors border border-charcoal-600"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verify Our Code
              </a>
            </div>
            
            <TrustIndicators />
          </div>

          {/* Form Section */}
          <div className="max-w-2xl mx-auto mb-12 sm:mb-16">
            <SecretCreatorForm onSuccess={handleSuccess} />
          </div>

          {/* Features Section */}
          <FeaturesSection />
          
          {/* Security Comparison */}
          <SecurityComparison />
        </div>
      </div>
    </>
  )
}